import { useMemo, useState } from "react";
import {
  X,
  Download,
  FileText,
  Loader2,
  BadgeCheck,
  CircleDollarSign,
  Calendar,
  Building2,
  FolderOpen,
  Filter,
  Check,
} from "lucide-react";
import { toast } from "react-toastify";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function safeDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "" : dt.toISOString();
}

function formatDateLong(dateLike) {
  const d = safeDate(dateLike);
  if (!d) return "-";
  return new Date(d).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatDateShort(dateLike) {
  const d = safeDate(dateLike);
  if (!d) return "-";
  return new Date(d).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatCurrency(amount) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(Number(amount) || 0);
}

function num(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
}

function normStatus(s) {
  return String(s || "draft").toLowerCase().trim();
}

function prettyInvoiceStatus(s) {
  const st = normStatus(s);
  if (st === "paid overdue" || st === "paidoverdue" || st === "paid_overdue") return "Paid Overdue";
  if (st === "draft") return "Draft";
  if (st === "issued") return "Issued";
  if (st === "sent") return "Sent";
  if (st === "overdue") return "Overdue";
  if (st === "paid") return "Paid";
  if (st === "void") return "Void";
  if (st === "cancelled") return "Cancelled";
  return st ? st[0].toUpperCase() + st.slice(1) : "-";
}

function invoiceStatusPill(st) {
  const s = normStatus(st);
  if (s === "paid" || s === "paid overdue" || s === "paid_overdue") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s === "sent") return "bg-blue-50 text-blue-700 border-blue-200";
  if (s === "issued") return "bg-sky-50 text-sky-700 border-sky-200";
  if (s === "overdue") return "bg-red-50 text-red-700 border-red-200";
  if (s === "draft") return "bg-slate-50 text-slate-700 border-slate-200";
  if (s === "void" || s === "cancelled") return "bg-zinc-50 text-zinc-700 border-zinc-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
}

function isInvoicePaid(st) {
  const s = normStatus(st);
  return s === "paid" || s === "paid overdue" || s === "paid_overdue";
}

function isInvoiceExcluded(st) {
  const s = normStatus(st);
  return s === "void" || s === "cancelled";
}

function getInvoiceAmount(inv) {
  return num(inv?.totals?.grandTotal ?? inv?.totals?.grand_total ?? inv?.grandTotal ?? inv?.grand_total);
}

function getPaymentDate(inv) {
  return (
    inv?.paymentDate ||
    inv?.payment_date ||
    inv?.paidAt ||
    inv?.paid_at ||
    inv?.payment?.paymentDate ||
    inv?.payment?.payment_date ||
    null
  );
}

function hasDoc(doc) {
  return Boolean(doc?.provider) && Boolean(doc?.bucket) && Boolean(doc?.storage_path) && Boolean(doc?.content_type);
}

function toOrdinal(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return "";

  const mod100 = x % 100;
  let suffix = "th";

  if (!(mod100 >= 11 && mod100 <= 13)) {
    const mod10 = x % 10;
    if (mod10 === 1) suffix = "st";
    else if (mod10 === 2) suffix = "nd";
    else if (mod10 === 3) suffix = "rd";
  }

  return `${x}${suffix} Term`;
}

function normalizeStatusValue(st) {
  const s = normStatus(st);
  if (s === "paid overdue" || s === "paid_overdue" || s === "paidoverdue") return "paid_overdue";
  return s;
}

const FILTER_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "issued", label: "Issued" },
  { value: "sent", label: "Sent" },
  { value: "overdue", label: "Overdue" },
  { value: "paid", label: "Paid" },
  { value: "paid_overdue", label: "Paid Overdue" },
  { value: "void", label: "Void" },
  { value: "cancelled", label: "Cancelled" },
];

export default function AdvanceViewModal({
  isOpen,
  onClose,
  sales,
  invoices = [],
  onDownloadReference,
  onDownloadSentDocument,
  onDownloadPaymentDocument,
}) {
  const [downloading, setDownloading] = useState(false);
  const [downloadingDocKey, setDownloadingDocKey] = useState("");
  const [activeTab, setActiveTab] = useState("sales");

  // filter dropdown
  const [filterOpen, setFilterOpen] = useState(false);

  // default: hide void/cancelled
  const [selectedStatuses, setSelectedStatuses] = useState(() => [
    "draft",
    "issued",
    "sent",
    "overdue",
    "paid",
    "paid_overdue",
  ]);

  const installments = useMemo(() => {
    return Array.isArray(sales?.installment_percentages) ? sales.installment_percentages : [];
  }, [sales?.installment_percentages]);

  const invoiceSummary = useMemo(() => {
    const rows = Array.isArray(invoices) ? invoices : [];

    const active = rows.filter((inv) => !isInvoiceExcluded(inv?.status));
    const paid = active.filter((inv) => isInvoicePaid(inv?.status));
    const unpaid = active.filter((inv) => !isInvoicePaid(inv?.status));

    const paidTotal = paid.reduce((s, inv) => s + getInvoiceAmount(inv), 0);
    const activeTotal = active.reduce((s, inv) => s + getInvoiceAmount(inv), 0);
    const remainingTotal = Math.max(0, activeTotal - paidTotal);

    const latestPaymentDate = paid
      .map((inv) => getPaymentDate(inv))
      .filter(Boolean)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

    return {
      active,
      paid,
      unpaid,
      counts: { active: active.length, paid: paid.length, unpaid: unpaid.length },
      totals: { activeTotal, paidTotal, remainingTotal },
      latestPaymentDate,
    };
  }, [invoices]);

  const filteredInvoicesForTable = useMemo(() => {
    const rows = Array.isArray(invoices) ? invoices : [];
    const set = new Set((selectedStatuses || []).map(String));
    return rows
      .filter((inv) => set.has(normalizeStatusValue(inv?.status)))
      .slice()
      .sort((a, b) => num(a?.invoice_index) - num(b?.invoice_index));
  }, [invoices, selectedStatuses]);

  const selectedLabels = useMemo(() => {
    const set = new Set(selectedStatuses || []);
    return FILTER_OPTIONS.filter((x) => set.has(x.value)).map((x) => x.label);
  }, [selectedStatuses]);

  // ✅ safe early return
  if (!isOpen || !sales) return null;

  const salesId = sales.sales_id || "-";
  const status = String(sales.status || "unpaid").toLowerCase();

  const creationDate = sales.creation_date || sales.createdAt;
  const termOfPayment = num(sales.term_of_payment);

  const refType = sales.reference_document_type || "PO";
  const ref = sales.reference_document || {};
  const projectCategory = sales.project_category || sales.projectCategory || "-";

  const clientName =
    sales.client_name ||
    sales.clientName ||
    sales.client?.client_name ||
    sales.client?.name ||
    sales.client_id?.client_name ||
    "-";

  const items = Array.isArray(sales.items) ? sales.items : [];
  const computedSubtotal = items.reduce((sum, it) => sum + num(it.amount), 0);

  const subtotal = Number.isFinite(Number(sales.subtotal)) ? num(sales.subtotal) : computedSubtotal;
  const totalDiscount = num(sales.total_discount);
  const totalPpn = num(sales.total_ppn);
  const totalPph = num(sales.total_pph);
  const grandTotal = Number.isFinite(Number(sales.grand_total))
    ? num(sales.grand_total)
    : Math.max(0, subtotal - totalDiscount + totalPpn - totalPph);

  const ppnPct = num(sales.ppn);
  const pphPct = num(sales.pph);
  const discPct = num(sales.discount);

  const statusConfig = {
    paid: { label: "Paid", pill: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: BadgeCheck },
    partially_paid: { label: "Partially Paid", pill: "bg-blue-50 text-blue-700 border-blue-200", icon: CircleDollarSign },
    unpaid: { label: "Unpaid", pill: "bg-orange-50 text-orange-700 border-orange-200", icon: CircleDollarSign },
    cancelled: { label: "Cancelled", pill: "bg-zinc-50 text-zinc-700 border-zinc-200", icon: CircleDollarSign },
  };

  const pillClass = statusConfig[status]?.pill || "bg-slate-50 text-slate-700 border-slate-200";
  const statusLabel = statusConfig[status]?.label || status;

  const handleDownloadRef = async () => {
  if (downloading) return;
  if (!onDownloadReference) return toast.info("Download reference belum di-wire.");
  if (!refDocOk) return toast.info("Reference document tidak tersedia.");

  try {
    setDownloading(true);
    await onDownloadReference(sales);
    toast.success("Reference document berhasil didownload.");
  } catch (err) {
    toast.error(err?.message || "Gagal download reference document");
  } finally {
    setDownloading(false);
  }
};


  const refDocOk = hasDoc(ref);
  const canDownloadRef = Boolean(onDownloadReference) && refDocOk;
  const handleDownloadSentDoc = async (inv) => {
    if (!inv) return;
    if (!onDownloadSentDocument) return toast.info("Download sent document belum di-wire.");
    const key = `sent:${inv?._id || inv?.invoice_index || "x"}`;
    if (downloadingDocKey) return;

    try {
      setDownloadingDocKey(key);
      await onDownloadSentDocument(inv);
      toast.success("Sent document berhasil didownload.");
    } catch (err) {
      toast.error(err?.message || "Gagal download sent document");
    } finally {
      setDownloadingDocKey("");
    }
  };

  const handleDownloadPaymentDoc = async (inv) => {
    if (!inv) return;
    if (!onDownloadPaymentDocument) return toast.info("Download payment document belum di-wire.");
    const key = `pay:${inv?._id || inv?.invoice_index || "x"}`;
    if (downloadingDocKey) return;

    try {
      setDownloadingDocKey(key);
      await onDownloadPaymentDocument(inv);
      toast.success("Payment document berhasil didownload.");
    } catch (err) {
      toast.error(err?.message || "Gagal download payment document");
    } finally {
      setDownloadingDocKey("");
    }
  };

  function toggleStatus(value) {
    setSelectedStatuses((prev) => {
      const v = String(value);
      const set = new Set(prev || []);
      if (set.has(v)) set.delete(v);
      else set.add(v);
      return Array.from(set);
    });
  }

  function setAllStatuses() {
    setSelectedStatuses(FILTER_OPTIONS.map((x) => x.value));
  }

  function clearStatuses() {
    setSelectedStatuses([]);
  }

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="bg-white rounded-2xl w-full max-w-7xl my-8 shadow-2xl overflow-visible">
        {/* Header */}
      <div className="bg-white border-b border-slate-200 rounded-t-2xl">

          <div className="px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">Detail Sales</h2>
                <p className="text-xs text-slate-500">{salesId}</p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={downloading}
              className={cx(
                "p-2 rounded-lg transition-colors",
                downloading ? "text-slate-300 cursor-not-allowed" : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              )}
              title="Tutup"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="px-5 flex gap-1 border-b border-slate-100">
            <button
              type="button"
              onClick={() => setActiveTab("sales")}
              className={cx(
                "px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
                activeTab === "sales"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
              )}
            >
              Detail Sales
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("invoices")}
              className={cx(
                "px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
                activeTab === "invoices"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
              )}
            >
              Invoice Summary
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {activeTab === "sales" && (
            <>
              {/* Sales Info Card */}
              <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-xl p-5">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                  <div className="space-y-3 flex-1">
                    <div>
                      <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-0.5">Sales ID</div>
                      <div className="text-xl font-bold text-slate-900">{salesId}</div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-start gap-2">
                        <div className="p-1.5 bg-white rounded-lg shadow-sm mt-0.5">
                          <Building2 className="w-3.5 h-3.5 text-slate-600" />
                        </div>
                        <div>
                          <div className="text-xs text-slate-500">Client</div>
                          <div className="text-sm font-semibold text-slate-900">{clientName}</div>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <div className="p-1.5 bg-white rounded-lg shadow-sm mt-0.5">
                          <Calendar className="w-3.5 h-3.5 text-slate-600" />
                        </div>
                        <div>
                          <div className="text-xs text-slate-500">Created Date</div>
                          <div className="text-sm font-semibold text-slate-900">{formatDateLong(creationDate)}</div>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <div className="p-1.5 bg-white rounded-lg shadow-sm mt-0.5">
                          <FolderOpen className="w-3.5 h-3.5 text-slate-600" />
                        </div>
                        <div>
                          <div className="text-xs text-slate-500">Project Category</div>
                          <div className="text-sm font-semibold text-slate-900">{projectCategory}</div>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <div className="p-1.5 bg-white rounded-lg shadow-sm mt-0.5">
                          <FileText className="w-3.5 h-3.5 text-slate-600" />
                        </div>
                        <div>
                          <div className="text-xs text-slate-500">Term of Payment</div>
                          <div className="text-sm font-semibold text-slate-900">{termOfPayment} bulan</div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <span className={cx("inline-flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-semibold border", pillClass)}>
                        {statusLabel}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 lg:w-72">
                    <div className="bg-white border border-slate-200 rounded-xl p-3">
                      <div className="text-xs text-slate-500 mb-0.5">Subtotal</div>
                      <div className="text-lg font-bold text-slate-900">{formatCurrency(subtotal)}</div>
                    </div>

                    <div className="bg-white border border-blue-200 rounded-xl p-3 shadow-sm">
                      <div className="text-xs text-blue-600 font-medium mb-0.5">Grand Total</div>
                      <div className="text-xl font-bold text-blue-600">{formatCurrency(grandTotal)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Products Table */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-visible">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900">Product / Items</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Daftar item dari Sales • Total: {items.length}</p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-900">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-white">Produk / Deskripsi</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-white w-24">Qty</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-white w-40">Harga</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-white w-40">Jumlah</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {items.map((it, idx) => {
                        const title = it.name || "Item";
                        const desc = it.description ? String(it.description) : "";
                        const merged = desc ? `${title} — ${desc}` : title;

                        return (
                          <tr key={`${idx}-${title}`} className="hover:bg-slate-50">
                            <td className="px-4 py-3 text-sm text-slate-900">{merged}</td>
                            <td className="px-4 py-3 text-sm text-center text-slate-900">{num(it.qty)}</td>
                            <td className="px-4 py-3 text-sm text-center text-slate-900">{formatCurrency(it.price)}</td>
                            <td className="px-4 py-3 text-sm text-center font-semibold text-slate-900">{formatCurrency(it.amount)}</td>
                          </tr>
                        );
                      })}

                      {!items.length && (
                        <tr>
                          <td colSpan={4} className="px-4 py-8 text-center text-slate-500 text-sm">
                            Tidak ada item.
                          </td>
                        </tr>
                      )}
                                      </tbody>

                    <tfoot>
                      <tr className="border-t border-slate-200 bg-white">
                        {/* Produk / Deskripsi + Qty + Harga */}
                        <td colSpan={3} className="px-4 py-3 text-right text-sm font-semibold text-slate-600">
                          Subtotal
                        </td>

                        {/* Jumlah (Amount) */}
                        <td className="px-4 py-3 text-center text-sm font-bold text-slate-900 whitespace-nowrap">
                          {formatCurrency(subtotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>

                </div>
              </div>

              {/* Reference + Ringkasan */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Reference Document</h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {refType} • {refDocOk ? (ref.reference_document || "-") : "Tidak Tersedia"}

                      </p>
                    </div>
                    <span
                      className={cx(
                        "inline-flex items-center gap-2 px-2.5 py-1 rounded-lg text-xs font-semibold border",
                        canDownloadRef ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-400 border-slate-200"
                      )}
                    >
                      {canDownloadRef ? "Tersedia" : "Tidak tersedia"}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <div className="text-xs font-medium text-slate-500 mb-0.5">Display / No. Dokumen</div>
                        <div className="text-sm text-slate-900">
  {refDocOk ? (ref.reference_document || "-") : "Tidak Tersedia"}
</div>

                      </div>

                      <div>
                        <div className="text-xs font-medium text-slate-500 mb-0.5">Reference Type</div>
                        <div className="text-sm text-slate-900">{refType}</div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <div className="text-xs font-medium text-slate-500 mb-0.5">Created</div>
                        <div className="text-sm text-slate-900">{creationDate ? formatDateShort(creationDate) : "-"}</div>
                      </div>

                      <div>
                        <button
                          type="button"
                          onClick={handleDownloadRef}
                          disabled={downloading || !canDownloadRef}
                          className={cx(
                            "inline-flex items-center gap-2 px-4 py-2 rounded-lg border transition-all text-sm font-medium w-full justify-center",
                            downloading || !canDownloadRef
                              ? "border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed"
                              : "border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 hover:border-blue-400"
                          )}
                          title={canDownloadRef ? "Download Reference" : "Reference document tidak tersedia"}
                        >
                          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                          {downloading ? "Downloading..." : "Download Reference"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">Ringkasan</h3>

                  <div className="border border-slate-200 rounded-xl p-3 bg-slate-50 mb-3">
                    <div className="text-xs font-medium text-slate-500 mb-2">Persentase</div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
                        <div className="text-[11px] text-slate-500">Discount</div>
                        <div className="text-sm font-bold text-slate-900">{discPct}%</div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
                        <div className="text-[11px] text-slate-500">PPN</div>
                        <div className="text-sm font-bold text-slate-900">{ppnPct}%</div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
                        <div className="text-[11px] text-slate-500">PPh</div>
                        <div className="text-sm font-bold text-slate-900">{pphPct}%</div>
                      </div>
                    </div>
                  </div>

                                <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Total PPN</span>
                      <span className="font-medium text-slate-900">{formatCurrency(totalPpn)}</span>
                    </div>

                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Total PPh</span>
                      <span className="font-medium text-slate-900">{formatCurrency(totalPph)}</span>
                    </div>

                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Total Discount</span>
                      <span className="font-medium text-slate-900">{formatCurrency(totalDiscount)}</span>
                    </div>

                    <div className="pt-2 mt-2 border-t border-slate-200">
                      <div className="flex justify-between">
                        <span className="text-sm font-semibold text-slate-900">Grand Total</span>
                        <span className="text-lg font-bold text-blue-600">{formatCurrency(grandTotal)}</span>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </>
          )}

          {activeTab === "invoices" && (
            <>
              {/* Invoice Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Total Aktif</div>
                  <div className="text-xl font-bold text-slate-900">{formatCurrency(invoiceSummary.totals.activeTotal)}</div>
                  <div className="mt-1 text-xs text-slate-500">{invoiceSummary.counts.active} invoice aktif</div>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="text-xs font-medium text-emerald-700 uppercase tracking-wide mb-1">Sudah Dibayar</div>
                  <div className="text-xl font-bold text-emerald-700">{formatCurrency(invoiceSummary.totals.paidTotal)}</div>
                  <div className="mt-1 text-xs text-emerald-600">{invoiceSummary.counts.paid} invoice paid</div>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <div className="text-xs font-medium text-orange-700 uppercase tracking-wide mb-1">Belum Dibayar</div>
                  <div className="text-xl font-bold text-orange-700">{formatCurrency(invoiceSummary.totals.remainingTotal)}</div>
                  <div className="mt-1 text-xs text-orange-600">{invoiceSummary.counts.unpaid} invoice unpaid</div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="text-xs font-medium text-blue-700 uppercase tracking-wide mb-1">Latest Payment</div>
                  <div className="text-base font-bold text-blue-700">
                    {invoiceSummary.latestPaymentDate ? formatDateShort(invoiceSummary.latestPaymentDate) : "-"}
                  </div>
                </div>
              </div>

              {/* Filter + Table */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-visible">
                {/* HEADER */}
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Daftar Invoice</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Menampilkan {filteredInvoicesForTable.length} invoice (kartu ringkasan di atas tetap mengabaikan void/cancelled).
                    </p>
                  </div>

                  {/* ✅ button + dropdown anchored together */}
                  <div className="relative z-[999]">
                    <button
                      type="button"
                      onClick={() => setFilterOpen((v) => !v)}
                      className={cx(
                        "inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all",
                        filterOpen
                          ? "border-blue-300 bg-blue-50 text-blue-700"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      )}
                      title="Filter status invoice"
                    >
                      <Filter className="w-4 h-4" />
                      Filter Status
                      <span className="ml-1 text-xs px-2 py-0.5 rounded-md border border-slate-200 bg-white">
                        {selectedStatuses.length}
                      </span>
                    </button>

                    {filterOpen && (
                      <div
                        className="absolute right-0 top-full mt-2 w-[320px] max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-xl shadow-xl p-3"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-semibold text-slate-900">Tampilkan status</div>
                          <button
                            type="button"
                            onClick={() => setFilterOpen(false)}
                            className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                            title="Tutup"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="flex items-center gap-2 mb-2">
                          <button
                            type="button"
                            onClick={setAllStatuses}
                            className="text-xs px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700"
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            onClick={clearStatuses}
                            className="text-xs px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700"
                          >
                            Clear
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          {FILTER_OPTIONS.map((opt) => {
                            const checked = selectedStatuses.includes(opt.value);
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => toggleStatus(opt.value)}
                                className={cx(
                                  "flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg border text-sm transition-all",
                                  checked
                                    ? "border-blue-200 bg-blue-50 text-blue-800"
                                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                                )}
                              >
                                <span className="text-xs font-medium">{opt.label}</span>
                                <span
                                  className={cx(
                                    "w-5 h-5 rounded-md border flex items-center justify-center",
                                    checked ? "border-blue-300 bg-blue-600 text-white" : "border-slate-200 bg-white text-transparent"
                                  )}
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </span>
                              </button>
                            );
                          })}
                        </div>

                        <div className="mt-3">
                          <div className="text-[11px] text-slate-500 mb-1">Selected</div>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedLabels.length ? (
                              selectedLabels.map((lbl) => (
                                <span
                                  key={lbl}
                                  className="inline-flex items-center px-2 py-0.5 rounded-md text-xs border border-slate-200 bg-slate-50 text-slate-700"
                                >
                                  {lbl}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-slate-400">Tidak ada (tabel akan kosong)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ✅ table wrapper keeps overflow-x-auto */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-900">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-white w-40">TOP</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-white">Invoice</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-white w-28">Installment</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-white w-32">Status</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-white w-36">Amount</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-white w-32">Due Date</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-white w-32">Payment Date</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-white w-44">Documents</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {filteredInvoicesForTable.length ? (
                        filteredInvoicesForTable.map((inv) => {
                          const invoiceNo = inv?.invoiceNo || inv?.issued_id || "-";
                          const invAmount = getInvoiceAmount(inv);
                          const payDate = getPaymentDate(inv);
                          const dueDate = inv?.dueDate || null;
                          const st = inv?.status;

                          const idx = num(inv?.invoice_index);
                          const installmentPct = idx >= 1 ? num(installments[idx - 1]) : 0;

                          const sentDocOk = hasDoc(inv?.sent_document);
                          const payDocOk = hasDoc(inv?.payment_document);

                          const sentKey = `sent:${inv?._id || idx || "x"}`;
                          const payKey = `pay:${inv?._id || idx || "x"}`;

                          const canDownloadSent = sentDocOk && Boolean(onDownloadSentDocument) && !downloadingDocKey;
                          const canDownloadPay = payDocOk && Boolean(onDownloadPaymentDocument) && !downloadingDocKey;

                          return (
                            <tr key={inv?._id || `${invoiceNo}-${inv?.invoice_index}`} className="hover:bg-slate-50">
                              <td className="px-4 py-3">
                                <div className="text-sm font-semibold text-slate-900">{toOrdinal(idx) || "-"}</div>
                                <div className="text-xs text-slate-500">#{idx || "-"}</div>
                              </td>

                              <td className="px-4 py-3">
                                <div className="text-sm font-semibold text-slate-900">{invoiceNo}</div>
                                <div className="text-xs text-slate-500">Due: {dueDate ? formatDateShort(dueDate) : "-"}</div>
                              </td>

                              <td className="px-4 py-3 text-center text-sm">
                                {installmentPct ? (
                                  <span className="font-semibold text-slate-900">{installmentPct}%</span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>

                              <td className="px-4 py-3 text-center">
                                <span className={cx("inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold border", invoiceStatusPill(st))}>
                                  {prettyInvoiceStatus(st)}
                                </span>
                              </td>

                              <td className="px-4 py-3 text-sm text-center font-semibold text-slate-900">{formatCurrency(invAmount)}</td>

                              <td className="px-4 py-3 text-sm text-center text-slate-900">{dueDate ? formatDateShort(dueDate) : "-"}</td>

                              <td className="px-4 py-3 text-sm text-center text-slate-900">{payDate ? formatDateShort(payDate) : "-"}</td>

                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadSentDoc(inv)}
                                    disabled={!canDownloadSent}
                                    className={cx(
                                      "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all",
                                      canDownloadSent
                                        ? "border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400"
                                        : "border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed"
                                    )}
                                    title={sentDocOk ? "Download Sent Document" : "Sent document tidak tersedia"}
                                  >
                                    {downloadingDocKey === sentKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                                    Sent
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => handleDownloadPaymentDoc(inv)}
                                    disabled={!canDownloadPay}
                                    className={cx(
                                      "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all",
                                      canDownloadPay
                                        ? "border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400"
                                        : "border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed"
                                    )}
                                    title={payDocOk ? "Download Payment Document" : "Payment document tidak tersedia"}
                                  >
                                    {downloadingDocKey === payKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                                    Payment
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={8} className="px-4 py-10 text-center text-slate-500 text-sm">
                            Tidak ada invoice untuk filter ini.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-200">
                  <p className="text-xs text-slate-500">
                    * "Paid" dihitung dari status <span className="font-semibold">paid</span> atau{" "}
                    <span className="font-semibold">Paid Overdue</span>. Kartu ringkasan di atas tetap mengabaikan void/cancelled.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
    <div className="bg-white border-t border-slate-200 px-5 py-3 rounded-b-2xl">

          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={downloading}
              className={cx(
                "px-5 py-2 rounded-lg transition-all text-sm font-medium",
                downloading ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-slate-900 text-white hover:bg-slate-800"
              )}
            >
              Tutup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
